// ─────────────────────────────────────────────
// ENUMS & CONSTANTS
// ─────────────────────────────────────────────

export type DayOfWeek = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun'

export const DAYS_OF_WEEK: DayOfWeek[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ISO day index (0 = Sun, 1 = Mon ... 6 = Sat) → DayOfWeek
export const JS_DAY_TO_DOW: DayOfWeek[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export type PriceTier = '$' | '$$' | '$$$' | '$$$$'

export type VerificationStatus = 'unverified' | 'community' | 'verified' | 'claimed'

export type DataSource = 'user_submitted' | 'business_claimed' | 'scraped' | 'admin'

export type DealType = 'beer' | 'wine' | 'cocktail' | 'liquor' | 'food' | 'general'

export type VenueCategory =
  | 'sports_bar'
  | 'dive_bar'
  | 'cocktail_bar'
  | 'rooftop'
  | 'restaurant'
  | 'brewery'
  | 'wine_bar'
  | 'pub'
  | 'lounge'
  | 'date_night'
  | 'patio'
  | 'live_music'
  | 'late_night'
  | 'waterfront'    // ← ADDED

// ─────────────────────────────────────────────
// DEAL ITEM — a single priced special
// ─────────────────────────────────────────────

export interface DealItem {
  type: DealType
  description: string
  price?: number
  original_price?: number
}

// ─────────────────────────────────────────────
// HAPPY HOUR SCHEDULE — one time block
// ─────────────────────────────────────────────

export interface HappyHourSchedule {
  id: string
  venue_id: string
  days: DayOfWeek[]
  start_time: string
  end_time: string
  is_all_day: boolean
  deal_text: string
  deals: DealItem[]
  created_at: string
  updated_at: string
}

// ─────────────────────────────────────────────
// BUSINESS HOURS — from Google Places API
// ─────────────────────────────────────────────

// day: 0=Sunday, 1=Monday ... 6=Saturday
// time: "HHMM" 24-hour format (e.g. "1130" = 11:30am)
export interface BusinessHourPeriod {
  open:  { day: number; time: string }
  close: { day: number; time: string } | null  // null = open 24 hours
}

// ─────────────────────────────────────────────
// VENUE — the core entity
// ─────────────────────────────────────────────

export interface Venue {
  id: string
  name: string
  neighborhood: string
  city: string
  state: string

  address: string | null
  latitude: number | null
  longitude: number | null

  website: string | null
  phone: string | null

  categories: VenueCategory[]
  price_tier: PriceTier | null

  image_url: string | null

  verification_status: VerificationStatus
  last_verified_at: string | null
  data_source: DataSource
  claimed_by_user_id: string | null

  dog_friendly: boolean
  is_featured: boolean
  is_sponsored: boolean
  upvote_count: number
  business_hours?: BusinessHourPeriod[] | null  // optional — not set on older venues

  created_at: string
  updated_at: string

  schedules?: HappyHourSchedule[]
}

// ─────────────────────────────────────────────
// FILTER STATE
// ─────────────────────────────────────────────

export interface TimeWindow {
  start: string
  end: string
}

export interface FilterState {
  search: string
  days: Set<DayOfWeek>
  dealTypes: Set<DealType>
  categories: Set<VenueCategory>
  neighborhoods: Set<string>
  priceTiers: Set<PriceTier>
  openNow: boolean
  dogFriendly: boolean          // ← ADDED (was as any before)
  timeWindow: TimeWindow | null
  city: string | null
}

export const DEFAULT_FILTERS: FilterState = {
  search: '',
  days: new Set(),
  dealTypes: new Set(),
  categories: new Set(),
  neighborhoods: new Set(),
  priceTiers: new Set(),
  openNow: false,
  dogFriendly: false,           // ← ADDED
  timeWindow: null,
  city: null,
}

// ─────────────────────────────────────────────
// SORT MODE
// ─────────────────────────────────────────────

export type SortMode =
  | 'best_match'
  | 'closest'
  | 'cheapest'
  | 'starting_soon'
  | 'most_popular'
  | 'featured'

export const SORT_LABELS: Record<SortMode, string> = {
  best_match:    'Best match',
  closest:       'Closest',
  cheapest:      'Cheapest',
  starting_soon: 'Starting soon',
  most_popular:  'Most popular',
  featured:      'Featured',
}

// ─────────────────────────────────────────────
// USER LOCATION
// ─────────────────────────────────────────────

export interface UserLocation {
  lat: number
  lng: number
  source: 'gps' | 'manual' | 'ip'
  label?: string
}

// ─────────────────────────────────────────────
// FORM STATE
// ─────────────────────────────────────────────

export interface VenueFormState {
  id: string | null
  name: string
  neighborhood: string
  city: string
  address: string
  website: string
  phone: string
  categories: VenueCategory[]
  price_tier: PriceTier | ''
  image_url: string
  schedule_id: string | null
  days: DayOfWeek[]
  start_time: string
  end_time: string
  is_all_day: boolean
  deal_text: string
  deals: DealItem[]
}

export const EMPTY_FORM: VenueFormState = {
  id: null,
  name: '',
  neighborhood: '',
  city: '',
  address: '',
  website: '',
  phone: '',
  categories: [],
  price_tier: '',
  image_url: '',
  schedule_id: null,
  days: [],
  start_time: '16:00',
  end_time: '18:00',
  is_all_day: false,
  deal_text: '',
  deals: [],
}

// ─────────────────────────────────────────────
// DISPLAY HELPERS
// ─────────────────────────────────────────────

export const DEAL_TYPE_LABELS: Record<DealType, string> = {
  beer:     'Beer',
  wine:     'Wine',
  cocktail: 'Cocktails',
  liquor:   'Liquor',
  food:     'Food',
  general:  'General',
}

export const DEAL_TYPE_COLORS: Record<DealType, { bg: string; text: string }> = {
  beer:     { bg: '#FEF3E2', text: '#633806' },
  wine:     { bg: '#F9EEFF', text: '#5B1A7A' },
  cocktail: { bg: '#EEEDFE', text: '#3C3489' },
  liquor:   { bg: '#FFF0F0', text: '#8B1A1A' },
  food:     { bg: '#E1F5EE', text: '#085041' },
  general:  { bg: '#F0EDE8', text: '#555' },
}

export const CATEGORY_LABELS: Record<VenueCategory, string> = {
  sports_bar:   'Sports Bar',
  dive_bar:     'Dive Bar',
  cocktail_bar: 'Cocktail Bar',
  rooftop:      'Rooftop',
  restaurant:   'Restaurant',
  brewery:      'Brewery',
  wine_bar:     'Wine Bar',
  pub:          'Pub',
  lounge:       'Lounge',
  date_night:   'Date Night',
  patio:        'Patio',
  live_music:   'Live Music',
  late_night:   'Late Night',
  waterfront:   'Waterfront',  // ← ADDED
}

export const PRICE_TIER_LABELS: Record<PriceTier, string> = {
  '$':    'Budget',
  '$$':   'Moderate',
  '$$$':  'Upscale',
  '$$$$': 'Fine Dining',
}

export type { HappyHourStatus, ScheduleStatus, StatusVisual } from '../utils/happeningNow'
